import { Component, computed, effect, input, signal } from '@angular/core';
import { PatientBehaviorModel, BehaviorOrderModel } from '../../core/api/models';
import { StaffApiService, SupervisorApiService } from '../../core/api/services';
import { AlertService } from '../../shared/services/alert.service';
import { SupervisorHelperService } from '../supervisor.helper.service';
import { Utils } from '../../core/utils';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { Router } from '@angular/router';
@Component({
  selector: 'app-behaviors',
  templateUrl: './behaviors.component.html',
  styleUrl: './behaviors.component.css',
  standalone: false,
})
export class BehaviorsComponent {
  patientID = input.required<string>();

  behaviors = signal<PatientBehaviorModel[]>([]);

  loadingBehaviors = signal<boolean>(false);

  searchKeyword = signal<string | null>(null);
  selectedFilters = signal<any[]>([]);

  includeDeleted = signal<boolean>(false);
  includeArchived = signal<boolean>(false);
  includeClosed = signal<boolean>(false);
  includeMastered = signal<boolean>(false);
  
  // UI State
  filtersCollapsed = signal<boolean>(true);
  
  // Selection functionality
  selectingForDeletion: boolean = false;
  behaviorIDsForDeletion: number[] = [];
  doSelectAll: boolean = false;
  
  // Order functionality
  orderMode = signal<'alphabetical' | 'status' | 'custom'>('alphabetical');
  isEditMode = signal<boolean>(false);
  pendingOrderChanges = signal<PatientBehaviorModel[]>([]);
  lastMovedBehaviorId = signal<number | null>(null);
  
  orderOptions = signal<SelectItem[]>([
    { label: 'Alphabetically', value: 'alphabetical' },
    { label: 'by Status', value: 'status' },
    { label: 'Custom', value: 'custom' }
  ]);

  possibleFilters = computed<SelectItemGroup[]>(() => {
    return [
      {
        label: 'Attributes',
        items: [
          {
            label: 'Is Deleted',
            value: 'IsDeleted',
            icon: 'pi pi-trash',
          },
        ],
      },
    ];
  });

  filteredBehaviors = computed(() => {
    let filteredBehaviors = this.behaviors();
    // use keyword from search bar for the first filter
    if (this.searchKeyword() != null && this.searchKeyword() != '') {
      filteredBehaviors = filteredBehaviors.filter((behavior) =>
        behavior.PatientBehaviorName?.toLowerCase().includes(
          this.searchKeyword()!.toLowerCase()
        )
      );
    }
    if (!this.includeDeleted()) {
      filteredBehaviors = filteredBehaviors.filter(
        (behavior) => !behavior.IsDeleted
      );
    }
    if (!this.includeArchived()) {
      filteredBehaviors = filteredBehaviors.filter(
        (behavior) => !behavior.IsArchived
      );
    }
    
    if (!this.includeMastered()) {
      filteredBehaviors = filteredBehaviors.filter(
        (behavior) => !behavior.IsMastered
      );
    }
    
    if (!this.includeClosed()) {
      filteredBehaviors = filteredBehaviors.filter(
        (behavior) => !behavior.IsClosed
      );
    }
    if (this.selectedFilters() == null) {
      return filteredBehaviors;
    }
    this.selectedFilters().forEach((selectedFilter) => {
      if (selectedFilter == 'IsDeleted') {
        filteredBehaviors = filteredBehaviors.filter(
          (behavior) => behavior.IsDeleted
        );
      }
    });

    // Apply sorting based on order mode
    if (this.orderMode() === 'alphabetical') {
      // order behaviors alphabetically
      filteredBehaviors.sort((a, b) => {
        return a.PatientBehaviorName?.localeCompare(b.PatientBehaviorName ?? '') ?? 0;
      });
    } else if (this.orderMode() === 'status') {
      // order behaviors by status (deleted, archived, mastered, closed, active)
      filteredBehaviors.sort((a, b) => {
        const getStatusOrder = (behavior: PatientBehaviorModel) => {
          if (behavior.IsDeleted) return 0;
          if (behavior.IsArchived) return 1;
          if (behavior.IsMastered) return 2;
          if (behavior.IsClosed) return 3;
          return 4; // active
        };
        return getStatusOrder(a) - getStatusOrder(b);
      });
    } else if (this.orderMode() === 'custom') {
      // order behaviors by OrderNr
      filteredBehaviors.sort((a, b) => {
        const aOrder = a.OrderNr ?? 0;
        const bOrder = b.OrderNr ?? 0;
        return aOrder - bOrder;
      });
    }

    return filteredBehaviors;
  });

  isExpandedStatus: { [key: number]: boolean } = {};

  onBehaviorExpandedChange(behaviorId: number, isExpanded: boolean) {
    this.isExpandedStatus[behaviorId] = isExpanded;
  }

  constructor(
    private staffApiService: StaffApiService,
    private supervisorApiService: SupervisorApiService,
    private alertService: AlertService,
    protected supervisorHelperService: SupervisorHelperService,
    private router: Router
  ) {
    effect(
      () => {
        console.log(
          'update signal triggered: ',
          this.supervisorHelperService.updateSignal()
        );
        this.loadData();
      },
      { allowSignalWrites: true }
    );

  }

  ngOnInit(): void {
    console.log('BehaviorsComponent ngOnInit');
    this.loadOrderModePreference();
    if (this.patientID()) {
      this.loadData();
    } else {
      this.router.navigate(['/select-patient']);
    }
  }

  loadData() {
    this.loadingBehaviors.set(true);
    this.staffApiService
      .GetPatientBehaviors({ patientID: Number(this.patientID()) })
      .subscribe(
        (behaviors) => {
          this.loadingBehaviors.set(false);
          this.behaviors.set(behaviors);
        },
        (err) => {
          this.loadingBehaviors.set(false);
          this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
        }
      );
  }

  addBehaviorButtonClicked() {
    console.log('addBehaviorButtonClicked');
    this.supervisorHelperService.addBehaviorButtonClicked(null);
  }

  getFromBIPButtonClicked() {
    console.log('getFromBIPButtonClicked');
    this.supervisorApiService
      .ImportBehaviorsFromBIP({ patientID: Number(this.patientID()) })
      .subscribe(
        (response) => {
          console.log('ImportBehaviorsFromBIP response: ', response);
          this.loadData();
        },
        (err) => {
          this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
        }
      );
  }

  clearSearch() {
    this.searchKeyword.set(null);
    this.selectedFilters.set([]);
    this.includeDeleted.set(false);
    this.includeArchived.set(false);
    this.exitEditModeIfActive();
  }

  exitEditModeIfActive() {
    if (this.isEditMode()) {
      this.isEditMode.set(false);
      this.pendingOrderChanges.set([]);
    }
  }

  toggleIncludeDeleted() {
    this.includeDeleted.set(!this.includeDeleted());
    this.exitEditModeIfActive();
  }

  toggleIncludeArchived() {
    this.includeArchived.set(!this.includeArchived());
    this.exitEditModeIfActive();
  }

  toggleIncludeMastered() {
    this.includeMastered.set(!this.includeMastered());
    this.exitEditModeIfActive();
  }

  toggleIncludeClosedAndMastered() {
    this.includeClosed.set(!this.includeClosed());
    this.includeMastered.set(!this.includeMastered());
    this.exitEditModeIfActive();
  }

  toggleIncludeClosed() {
    this.includeClosed.set(!this.includeClosed());
    this.exitEditModeIfActive();
  }

  toggleFiltersCollapsed(): void {
    this.filtersCollapsed.set(!this.filtersCollapsed());
  }

  // Order functionality methods
  onOrderModeChange() {
    // Save the order mode preference
    this.saveOrderModePreference();
  }

  toggleEditMode() {
    if (this.isEditMode()) {
      // Exiting edit mode - save all pending changes
      this.saveAllPendingChanges();
    } else {
      // Starting edit mode - check if filters are active
      if (this.hasActiveFilters()) {
        this.alertService.confirm(
          'Clear Filters',
          'To start configuring a custom order for this list we must first clear the filters, click "yes" to reset the filters and start editing, "no" to cancel'
        ).subscribe((result) => {
          if (result) {
            this.clearAllFilters();
            this.isEditMode.set(true);
          }
        });
        return;
      }
    }
    this.isEditMode.set(!this.isEditMode());
  }

  private hasActiveFilters(): boolean {
    return (
      (this.selectedFilters() && this.selectedFilters().length > 0) ||
      (this.searchKeyword() && this.searchKeyword() !== '') ||
      this.includeDeleted() ||
      this.includeArchived() ||
      this.includeClosed() ||
      this.includeMastered()
    );
  }

  private clearAllFilters(): void {
    this.selectedFilters.set([]);
    this.searchKeyword.set(null);
    this.includeDeleted.set(false);
    this.includeArchived.set(false);
    this.includeClosed.set(false);
    this.includeMastered.set(false);
  }

  moveBehaviorUp(behaviorId: number) {
    if (this.orderMode() === 'custom') {
      const behaviors = this.filteredBehaviors();
      const currentIndex = behaviors.findIndex(b => b.ID === behaviorId);
      
      if (currentIndex > 0) {
        // Track the last moved behavior
        this.lastMovedBehaviorId.set(behaviorId);
        
        // Swap with previous behavior
        const temp = behaviors[currentIndex];
        behaviors[currentIndex] = behaviors[currentIndex - 1];
        behaviors[currentIndex - 1] = temp;
        
        // Update OrderNr for all behaviors
        behaviors.forEach((b, index) => {
          b.OrderNr = index + 1;
        });
        
        // Add all behaviors to pending changes
        this.addToPendingChanges(behaviors);
      }
    }
  }

  moveBehaviorDown(behaviorId: number) {
    if (this.orderMode() === 'custom') {
      const behaviors = this.filteredBehaviors();
      const currentIndex = behaviors.findIndex(b => b.ID === behaviorId);
      
      if (currentIndex < behaviors.length - 1) {
        // Track the last moved behavior
        this.lastMovedBehaviorId.set(behaviorId);
        
        // Swap with next behavior
        const temp = behaviors[currentIndex];
        behaviors[currentIndex] = behaviors[currentIndex + 1];
        behaviors[currentIndex + 1] = temp;
        
        // Update OrderNr for all behaviors
        behaviors.forEach((b, index) => {
          b.OrderNr = index + 1;
        });
        
        // Add all behaviors to pending changes
        this.addToPendingChanges(behaviors);
      }
    }
  }

  moveBehaviorToTop(behaviorId: number) {
    if (this.orderMode() === 'custom') {
      const behaviors = this.filteredBehaviors();
      const currentIndex = behaviors.findIndex(b => b.ID === behaviorId);
      
      if (currentIndex > 0) {
        // Track the last moved behavior
        this.lastMovedBehaviorId.set(behaviorId);
        
        // Move to top
        const behavior = behaviors.splice(currentIndex, 1)[0];
        behaviors.unshift(behavior);
        
        // Update OrderNr for all behaviors
        behaviors.forEach((b, index) => {
          b.OrderNr = index + 1;
        });
        
        // Add all affected behaviors to pending changes
        this.addToPendingChanges(behaviors.slice(0, currentIndex + 1));
      }
    }
  }

  moveBehaviorToBottom(behaviorId: number) {
    if (this.orderMode() === 'custom') {
      const behaviors = this.filteredBehaviors();
      const currentIndex = behaviors.findIndex(b => b.ID === behaviorId);
      
      if (currentIndex < behaviors.length - 1) {
        // Track the last moved behavior
        this.lastMovedBehaviorId.set(behaviorId);
        
        // Move to bottom
        const behavior = behaviors.splice(currentIndex, 1)[0];
        behaviors.push(behavior);
        
        // Update OrderNr for all behaviors
        behaviors.forEach((b, index) => {
          b.OrderNr = index + 1;
        });
        
        // Add all affected behaviors to pending changes
        this.addToPendingChanges(behaviors.slice(currentIndex));
      }
    }
  }

  private addToPendingChanges(behaviors: PatientBehaviorModel[]) {
    const currentPending = this.pendingOrderChanges();
    const newPending = [...currentPending];
    
    behaviors.forEach(behavior => {
      const existingIndex = newPending.findIndex(b => b.ID === behavior.ID);
      if (existingIndex >= 0) {
        newPending[existingIndex] = behavior;
      } else {
        newPending.push(behavior);
      }
    });
    
    this.pendingOrderChanges.set(newPending);
  }

  private saveAllPendingChanges(): void {
    const pendingChanges = this.pendingOrderChanges();
    
    if (pendingChanges.length === 0) {
      return;
    }

    // Get all behaviors (not just filtered ones) to ensure proper order values for all items
    const allBehaviors = this.behaviors();
    
    // Create order dictionary with all behaviors
    const behaviorOrders: { [key: string]: number | null } = {};
    
    // First, set order for all behaviors based on their current OrderNr
    allBehaviors.forEach(behavior => {
      if (behavior.OrderNr !== null && behavior.OrderNr !== undefined) {
        behaviorOrders[behavior.ID!.toString()] = behavior.OrderNr;
      }
    });
    
    // Then update with the pending changes
    pendingChanges.forEach(behavior => {
      if (behavior.OrderNr !== null && behavior.OrderNr !== undefined) {
        behaviorOrders[behavior.ID!.toString()] = behavior.OrderNr;
      }
    });

    // Create the order model
    const orderModel: BehaviorOrderModel = {
      PatientID: Number(this.patientID()),
      BehaviorOrders: behaviorOrders
    };

    // Save all order changes in a single call
    this.staffApiService.SaveBehaviorOrder({
      body: orderModel
    }).subscribe({
      next: () => {
        console.log('All behavior order changes saved successfully');
        // Clear pending changes
        this.pendingOrderChanges.set([]);
        // Trigger a refresh to get the updated data
        this.loadData();
      },
      error: (error) => {
        console.error('Error saving behavior order changes:', error);
        this.alertService.error('Failed to save behavior order changes');
      }
    });
  }

  private saveOrderModePreference(): void {
    const patientId = this.patientID();
    const orderMode = this.orderMode();
    if (patientId) {
      sessionStorage.setItem(`behaviors_order_mode_${patientId}`, orderMode);
    }
  }

  private loadOrderModePreference(): void {
    const patientId = this.patientID();
    if (patientId) {
      const savedOrderMode = sessionStorage.getItem(`behaviors_order_mode_${patientId}`) as 'alphabetical' | 'status' | 'custom';
      if (savedOrderMode && ['alphabetical', 'status', 'custom'].includes(savedOrderMode)) {
        this.orderMode.set(savedOrderMode);
      }
    }
  }

  // Selection functionality methods
  toggleIsSelecting(): void {
    this.selectingForDeletion = !this.selectingForDeletion;
  }

  cancelIsSelecting(): void {
    this.selectingForDeletion = false;
    this.behaviorIDsForDeletion = [];
  }

  selectAllBehaviors(): void {
    this.doSelectAll = !this.doSelectAll;
    if (this.doSelectAll == false)
      this.behaviorIDsForDeletion = [];
  }

  deleteSelectedBehaviors(): void {
    if(this.selectingForDeletion && this.behaviorIDsForDeletion.length > 0){
      // First confirmation: Ask about deletion
      this.alertService.confirm(
        'Confirm Deletion',
        `Are you sure you want to mark ${this.behaviorIDsForDeletion.length} behavior(s) as deleted?`
      ).subscribe((confirmed) => {
        if (confirmed) {
          this.loadingBehaviors.set(true);
          this.staffApiService.MarkMultiplePatientBehaviorsAsDeleted({
            body: {
              PatientID: Number(this.patientID()),
              BehaviorIDs: this.behaviorIDsForDeletion
            }
          })
            .subscribe({
              next: (result) => {
                this.alertService.success('Behaviors marked as deleted');
                this.supervisorHelperService.triggerUpdateSignal();
                this.cancelIsSelecting();
              },
              complete: () => {
                this.loadingBehaviors.set(false);
              },
              error: (error) => {
                console.error(error);
                this.alertService.error('Failed to mark behaviors as deleted');
                this.loadingBehaviors.set(false);
                this.supervisorHelperService.triggerUpdateSignal();
              }
            });
        }
      });
    }
  }

  selectBehavior(behaviorID: number): void {
    if( this.behaviorIDsForDeletion.includes(behaviorID) ){
      this.behaviorIDsForDeletion = this.behaviorIDsForDeletion.filter(id => id !== behaviorID)
    }
    else {
      this.behaviorIDsForDeletion.push(behaviorID);
    }
    console.log(this.behaviorIDsForDeletion);
  }

  isFirstBehavior(behaviorId: number): boolean {
    return this.filteredBehaviors().findIndex(b => b.ID === behaviorId) === 0;
  }

  isLastBehavior(behaviorId: number): boolean {
    return this.filteredBehaviors().findIndex(b => b.ID === behaviorId) === this.filteredBehaviors().length - 1;
  }

  hasPendingChanges(behaviorId: number): boolean {
    return this.lastMovedBehaviorId() === behaviorId;
  }

  getTotalBehaviorsCount(): number {
    return this.filteredBehaviors().length;
  }
}
